import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { IInventoryCostCenterModal } from 'pages/Inventory/CostCenter/InventoryCostCenterModal'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  ICostCenterPostPatchRequest,
  IGetAllCostCenterItemsSingleResponse,
} from 'types/api/inventoryCostCenterApi.interface'
import { IInventoryCostCenterSlice } from 'types/inventory.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, getParams, handleErrors } from 'utils/helpers'

const initialState: IInventoryCostCenterSlice = {
  isLoading: false,
  costCenterItems: [],
  totalCount: 0,
  totalPages: 0,
}

export const getCostCenterItems = createAsyncThunk(
  'inventoryCostCenter/getCostCenterItems',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllCostCenterItemsSingleResponse>> =
        await baseUrl.get('inventory/cost-center', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addCostCenterItem = createAsyncThunk(
  'inventoryCostCenter/postInventoryCostCenter',
  async (data: IInventoryCostCenterModal, thunkAPI) => {
    try {
      const request: ICostCenterPostPatchRequest = {
        name: data.name,
        code: data.code,
        ownerUserId: data.ownerUser.value,
      }
      const resp: AxiosResponse = await baseUrl.post('inventory/cost-center', request)
      await thunkAPI.dispatch(getCostCenterItems(getParams({ location, limit: 30 })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editCostCenterItem = createAsyncThunk(
  'inventoryCostCenter/patchInventoryCostCenter',
  async (data: IInventoryCostCenterModal, thunkAPI) => {
    try {
      const { id } = data
      delete data.id
      const request: ICostCenterPostPatchRequest = {
        name: data.name,
        code: data.code,
        ownerUserId: data.ownerUser.value,
      }
      const resp: AxiosResponse<IGetAllCostCenterItemsSingleResponse> = await baseUrl.patch(
        'inventory/cost-center/' + id,
        request,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteCostCenterItem = createAsyncThunk(
  'inventoryCostCenter/deleteInventoryCostCenter',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetAllCostCenterItemsSingleResponse> = await baseUrl.delete(
        'inventory/cost-center/' + id,
      )
      await thunkAPI.dispatch(getCostCenterItems(getParams({ location, limit: 30 })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const inventoryCostCenterSlice = createSlice({
  name: 'inventoryCostCenter',
  initialState,
  reducers: {
    clearCostCenter: (state) => {
      state.costCenterItems = []
      state.totalCount = 0
      state.totalPages = 0
    },
  },
  extraReducers: {
    [getCostCenterItems.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCostCenterItems.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.costCenterItems = payload.data
      state.totalPages = payload.totalPages
      state.totalCount = payload.totalCount
    },
    [getCostCenterItems.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addCostCenterItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [addCostCenterItem.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Cost center item successfully added')
    },
    [addCostCenterItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editCostCenterItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [editCostCenterItem.fulfilled.type]: (
      state: IInventoryCostCenterSlice,
      { payload }: { payload: IGetAllCostCenterItemsSingleResponse },
    ) => {
      state.isLoading = false
      const index = state.costCenterItems.findIndex((i) => i.id == payload.id)
      state.costCenterItems[index] = payload
      toast.success('Cost center item successfully updated')
    },
    [editCostCenterItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteCostCenterItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteCostCenterItem.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Cost center item successfully deleted')
    },
    [deleteCostCenterItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearCostCenter } = inventoryCostCenterSlice.actions

export default inventoryCostCenterSlice.reducer
